import { EMainProductCodeType } from '@webtrader/core/api/config/company';
import { EDataTestId } from '@webtrader/core/constants/test';
import i18n from '@webtrader/core/i18n';

export enum EChartPeriod {
  MINUTE_1 = 'PERIOD_M1',
  MINUTE_2 = 'PERIOD_M2',
  MINUTE_3 = 'PERIOD_M3',
  MINUTE_4 = 'PERIOD_M4',
  MINUTE_5 = 'PERIOD_M5',
  MINUTE_6 = 'PERIOD_M6',
  MINUTE_10 = 'PERIOD_M10',
  MINUTE_15 = 'PERIOD_M15',
  MINUTE_20 = 'PERIOD_M20',
  MINUTE_30 = 'PERIOD_M30',
  HOUR_1 = 'PERIOD_H1',
  HOUR_2 = 'PERIOD_H2',
  HOUR_3 = 'PERIOD_H3',
  HOUR_4 = 'PERIOD_H4',
  HOUR_6 = 'PERIOD_H6',
  DAY = 'PERIOD_D1',
  WEEK = 'PERIOD_W1',
  MONTH = 'PERIOD_MN1'
}

const basePeriods = [
  EChartPeriod.MINUTE_1,
  EChartPeriod.MINUTE_5,
  EChartPeriod.MINUTE_15,
  EChartPeriod.MINUTE_30,
  EChartPeriod.HOUR_1,
  EChartPeriod.HOUR_4,
  EChartPeriod.DAY,
  EChartPeriod.WEEK,
  EChartPeriod.MONTH
];

const getSortedChartPeriod = (list: EChartPeriod[] = []) => {
  return list.sort((a, b) => Object.values(EChartPeriod).indexOf(a) - Object.values(EChartPeriod).indexOf(b));
};

export const interactablePeriods = {
  basePeriods,
  metaTrader5Periods: getSortedChartPeriod([
    ...basePeriods,
    EChartPeriod.MINUTE_2,
    EChartPeriod.MINUTE_3,
    EChartPeriod.MINUTE_4,
    EChartPeriod.MINUTE_6,
    EChartPeriod.MINUTE_10,
    EChartPeriod.MINUTE_20,
    EChartPeriod.HOUR_2,
    EChartPeriod.HOUR_3,
    EChartPeriod.HOUR_6
  ]),
  get [EMainProductCodeType.FTX]() {
    return this.basePeriods;
  },
  get [EMainProductCodeType.METATRADER4]() {
    return this.basePeriods;
  },
  get [EMainProductCodeType.METATRADER5]() {
    return this.metaTrader5Periods;
  },
  get [EMainProductCodeType.CENTROID]() {
    return this.basePeriods;
  },
  get [EMainProductCodeType.GENERIC]() {
    return this.basePeriods;
  },
  get [EMainProductCodeType.HORIZON]() {
    return this.basePeriods;
  }
};

/* eslint-disable-next-line complexity */
export const mapPeriodToText = (period: EChartPeriod, isNative?: boolean, lng?: string) => {
  switch (period) {
    case EChartPeriod.MINUTE_1:
      return isNative ? i18n.t('{{period}}m', { period: 1, lng }) : i18n.t('{{period}}min', { period: 1, lng });
    case EChartPeriod.MINUTE_2:
      return isNative ? i18n.t('{{period}}m', { period: 2, lng }) : i18n.t('{{period}}min', { period: 2, lng });
    case EChartPeriod.MINUTE_3:
      return isNative ? i18n.t('{{period}}m', { period: 3, lng }) : i18n.t('{{period}}min', { period: 3, lng });
    case EChartPeriod.MINUTE_4:
      return isNative ? i18n.t('{{period}}m', { period: 4, lng }) : i18n.t('{{period}}min', { period: 4, lng });
    case EChartPeriod.MINUTE_5:
      return isNative ? i18n.t('{{period}}m', { period: 5, lng }) : i18n.t('{{period}}min', { period: 5, lng });
    case EChartPeriod.MINUTE_6:
      return isNative ? i18n.t('{{period}}m', { period: 6, lng }) : i18n.t('{{period}}min', { period: 6, lng });
    case EChartPeriod.MINUTE_10:
      return isNative ? i18n.t('{{period}}m', { period: 10, lng }) : i18n.t('{{period}}min', { period: 10, lng });
    case EChartPeriod.MINUTE_15:
      return isNative ? i18n.t('{{period}}m', { period: 15, lng }) : i18n.t('{{period}}min', { period: 15, lng });
    case EChartPeriod.MINUTE_20:
      return isNative ? i18n.t('{{period}}m', { period: 20, lng }) : i18n.t('{{period}}min', { period: 20, lng });
    case EChartPeriod.MINUTE_30:
      return isNative ? i18n.t('{{period}}m', { period: 30, lng }) : i18n.t('{{period}}min', { period: 30, lng });
    case EChartPeriod.HOUR_1:
      return i18n.t('{{period}}H', { period: 1, lng });
    case EChartPeriod.HOUR_2:
      return i18n.t('{{period}}H', { period: 2, lng });
    case EChartPeriod.HOUR_3:
      return i18n.t('{{period}}H', { period: 3, lng });
    case EChartPeriod.HOUR_4:
      return i18n.t('{{period}}H', { period: 4, lng });
    case EChartPeriod.HOUR_6:
      return i18n.t('{{period}}H', { period: 6, lng });
    case EChartPeriod.DAY:
      return i18n.t('{{period}}D', { period: 1, lng });
    case EChartPeriod.WEEK:
      return i18n.t('{{period}}W', { period: 1, lng });
    case EChartPeriod.MONTH:
      return i18n.t('{{period}}M', { period: 1, lng });
    default:
      return '';
  }
};

/* eslint-disable-next-line complexity */
export const mapPeriodToFullText = (period: EChartPeriod, lng?: string) => {
  switch (period) {
    case EChartPeriod.MINUTE_1:
      return i18n.t('{{period}} minute', { period: 1, lng });
    case EChartPeriod.MINUTE_2:
      return i18n.t('{{period}} minutes', { period: 2, lng });
    case EChartPeriod.MINUTE_3:
      return i18n.t('{{period}} minutes', { period: 3, lng });
    case EChartPeriod.MINUTE_4:
      return i18n.t('{{period}} minutes', { period: 4, lng });
    case EChartPeriod.MINUTE_5:
      return i18n.t('{{period}} minutes', { period: 5, lng });
    case EChartPeriod.MINUTE_6:
      return i18n.t('{{period}} minutes', { period: 6, lng });
    case EChartPeriod.MINUTE_10:
      return i18n.t('{{period}} minutes', { period: 10, lng });
    case EChartPeriod.MINUTE_15:
      return i18n.t('{{period}} minutes', { period: 15, lng });
    case EChartPeriod.MINUTE_20:
      return i18n.t('{{period}} minutes', { period: 20, lng });
    case EChartPeriod.MINUTE_30:
      return i18n.t('{{period}} minutes', { period: 30, lng });
    case EChartPeriod.HOUR_1:
      return i18n.t('{{period}} hour', { period: 1, lng });
    case EChartPeriod.HOUR_2:
      return i18n.t('{{period}} hours', { period: 2, lng });
    case EChartPeriod.HOUR_3:
      return i18n.t('{{period}} hours', { period: 3, lng });
    case EChartPeriod.HOUR_4:
      return i18n.t('{{period}} hours', { period: 4, lng });
    case EChartPeriod.HOUR_6:
      return i18n.t('{{period}} hours', { period: 6, lng });
    case EChartPeriod.DAY:
      return i18n.t('{{period}} day', { period: 1, lng });
    case EChartPeriod.WEEK:
      return i18n.t('{{period}} week', { period: 1, lng });
    case EChartPeriod.MONTH:
      return i18n.t('{{period}} month', { period: 1, lng });
    default:
      return '';
  }
};
export const periodToTestIdMap: Record<EChartPeriod, EDataTestId> = {
  [EChartPeriod.MINUTE_1]: EDataTestId.CHART_PERIOD_OPTION_1_MINUTE,
  [EChartPeriod.MINUTE_2]: EDataTestId.CHART_PERIOD_OPTION_2_MINUTE,
  [EChartPeriod.MINUTE_3]: EDataTestId.CHART_PERIOD_OPTION_3_MINUTE,
  [EChartPeriod.MINUTE_4]: EDataTestId.CHART_PERIOD_OPTION_4_MINUTE,
  [EChartPeriod.MINUTE_5]: EDataTestId.CHART_PERIOD_OPTION_5_MINUTE,
  [EChartPeriod.MINUTE_6]: EDataTestId.CHART_PERIOD_OPTION_6_MINUTE,
  [EChartPeriod.MINUTE_10]: EDataTestId.CHART_PERIOD_OPTION_10_MINUTE,
  [EChartPeriod.MINUTE_15]: EDataTestId.CHART_PERIOD_OPTION_15_MINUTE,
  [EChartPeriod.MINUTE_20]: EDataTestId.CHART_PERIOD_OPTION_20_MINUTE,
  [EChartPeriod.MINUTE_30]: EDataTestId.CHART_PERIOD_OPTION_30_MINUTE,
  [EChartPeriod.HOUR_1]: EDataTestId.CHART_PERIOD_OPTION_1_HOUR,
  [EChartPeriod.HOUR_2]: EDataTestId.CHART_PERIOD_OPTION_2_HOUR,
  [EChartPeriod.HOUR_3]: EDataTestId.CHART_PERIOD_OPTION_3_HOUR,
  [EChartPeriod.HOUR_4]: EDataTestId.CHART_PERIOD_OPTION_4_HOUR,
  [EChartPeriod.HOUR_6]: EDataTestId.CHART_PERIOD_OPTION_6_HOUR,
  [EChartPeriod.DAY]: EDataTestId.CHART_PERIOD_OPTION_1_DAY,
  [EChartPeriod.WEEK]: EDataTestId.CHART_PERIOD_OPTION_1_WEEK,
  [EChartPeriod.MONTH]: EDataTestId.CHART_PERIOD_OPTION_1_MONTH
};
